import { createSignal, type Accessor } from 'solid-js';
import type { ParsedPairing } from './parse-hash';
import type { RelayWs, WsClose, WsMessage } from '../relay/ws';
import { classifyCloseCode, OPEN_TIMEOUT_MS, parseRelayNotification } from '../relay/ws';

export type ErrorCode =
  | 'no_hash'
  | 'malformed_hash'
  | 'wasm_load_failed'
  | 'ws_open_failed'
  | 'unknown_session'
  | 'room_busy'
  | 'rate_limited'
  | 'handshake_failed'
  | 'handshake_timeout'
  | 'room_expired'
  | 'reconnect_denied'
  | 'forgotten'
  | 'expired'
  | 'unreachable'
  | 'disconnected';

/** How long to wait for each handshake reply. SPEC §B.7.4 allows 15 s; a client may give up sooner. */
export const HANDSHAKE_REPLY_TIMEOUT_MS = 10_000;

/**
 * A socket can die without `onclose` (Wi-Fi→LTE often fires no `offline`/`online`), so a live
 * session that hears nothing for this long is treated as closed. Even a paused agent answers
 * the 10 s app ping, so this is two missed pongs.
 */
export const SILENCE_TIMEOUT_MS = 20_000;
/** With `--ask-trust-device` the agent waits on its prompt between Noise msg2 and hello, reading nothing. */
export const PRE_HELLO_SILENCE_TIMEOUT_MS = 120_000;

/**
 * A network flap usually heals within seconds, so for the first minute of an outage the retry
 * gap stays short; after that the old exponential schedule spares the relay.
 */
export const FAST_RETRY_WINDOW_MS = 60_000;
export const FAST_RETRY_CAP_MS = 5_000;
/** Time, not attempt count, bounds a reconnect episode: slow attempts (10 s timeouts) must not stretch it. */
export const RECONNECT_BUDGET_MS = 210_000;
/**
 * The budget is wall-clock, but a frozen tab (phone locked under "Reconnecting…") runs no
 * timers: a pause or an attempt that overran its plan by this much means the loop slept
 * through the outage, and the episode starts over instead of ending with nothing tried.
 */
export const FROZEN_TAB_SLACK_MS = 30_000;
const ATTEMPT_MAX_MS = OPEN_TIMEOUT_MS + HANDSHAKE_REPLY_TIMEOUT_MS;

/**
 * A visible page on the Unreachable screen tries one silent reconnect this often, so a returning
 * agent is found without a click (SPEC §H.7.2). The jitter keeps dashboards from dialling in step
 * after a relay restart.
 */
export const UNREACHABLE_CHECK_MS = 30_000;
export const UNREACHABLE_CHECK_JITTER_MS = 5_000;
/** An agent that sent `agent_stopping` is most likely restarting (an update, a service restart), so the first minute checks faster. */
export const STOPPED_CHECK_MS = 5_000;
export const STOPPED_CHECK_JITTER_MS = 1_000;
export const STOPPED_FAST_WINDOW_MS = 60_000;

/** A live session rotates its send key this often (SPEC §B.11), and also on the frame-count trigger. */
export const REKEY_INTERVAL_MS = 900_000;
/** SPEC §B.12 `decrypt_failure`: the session is over, though a new one may well work. */
const DECRYPT_FAILURE_CLOSE_CODE = 4505;

/** The agent closed the Noise channel (opcode 0x04): the session ends as if its socket had closed. */
export class PeerClosed extends Error {}

export type AppState =
  | { kind: 'idle' }
  | { kind: 'connecting'; sid: string }
  | { kind: 'handshaking'; sid: string; agentFp: string }
  | { kind: 'live'; agentFp: string; browserFp: string }
  | { kind: 'reconnecting'; agentFp: string; browserFp: string }
  | { kind: 'unreachable'; agentFp: string; browserFp: string }
  | { kind: 'error'; code: ErrorCode; detail?: string };

/** The transport of a live session, whichever handshake set it up. Keys and opcodes stay behind it. */
export interface LiveTransport {
  /**
   * Plaintext of a JSON frame, or null for a rekey frame (already applied). Throws `PeerClosed`
   * for a peer close; any other throw is a frame that failed to decrypt.
   */
  decrypt_frame(b: Uint8Array): Uint8Array | null;
  /** Encrypt a plaintext application frame (post-handshake). */
  encrypt_frame(plaintext: Uint8Array): Uint8Array;
  /**
   * A rekey frame under the current key. The send key rotates with it, so it goes out before
   * anything encrypted afterwards.
   */
  rekey_frame(): Uint8Array;
  /** The send counter reached the frame-count rekey threshold. */
  rekey_due(): boolean;
}

export interface HandshakeAdapter extends LiveTransport {
  spake_msg_a(): Uint8Array;
  process_spake_msg_b(b: Uint8Array): Uint8Array;
  process_noise_msg2(b: Uint8Array): void;
  /** Derive the reconnect root once the session is established (for persistence). */
  reconnect_root(): Uint8Array;
}

/** Two-frame reconnect handshake over a fresh relay room (no PIN). */
export interface ReconnectAdapter extends LiveTransport {
  msg1(): Uint8Array;
  process_msg2(b: Uint8Array): void;
}

export interface DriverDeps {
  ensureWasm: () => Promise<void>;
  openWs: (sid: string) => Promise<RelayWs>;
  makeHandshake: (
    pin: string,
    browserPriv: Uint8Array,
    agentPub: Uint8Array,
  ) => { hs: HandshakeAdapter; agentFingerprint: string };
  browserFingerprint: (browserPriv: Uint8Array) => string;
  dispatchFrame: (frame: unknown) => void;
  makeReconnect: (
    reconnectRoot: Uint8Array,
    browserPriv: Uint8Array,
    agentPub: Uint8Array,
  ) => ReconnectAdapter;
  rendezvousId: (reconnectRoot: Uint8Array) => string;
  persistReconnect: (agentPub: Uint8Array, reconnectRoot: Uint8Array) => Promise<void>;
  clearReconnect: () => Promise<void>;
  verifyDenyMac: (reconnectRoot: Uint8Array, reason: string, rendezvousId: string, mac: Uint8Array) => boolean;
  /** Default `REKEY_INTERVAL_MS`; shorter only for the e2e rekey scenario. */
  rekeyIntervalMs?: number;
}

export class PairingDriver {
  readonly state: Accessor<AppState>;
  private readonly setState: (s: AppState) => void;
  private ws: RelayWs | null = null;

  // Reconnect material — captured on first pairing or supplied to reconnect().
  private browserPriv: Uint8Array | null = null;
  private agentPub: Uint8Array | null = null;
  private reconnectRoot: Uint8Array | null = null;
  private agentFp = '';
  private browserFp = '';
  private retry = 0;
  private episodeStart = 0;
  private reconnecting = false;
  private wakeup: (() => void) | null = null;
  private _removeWakeups: (() => void) | null = null;

  // Checks on the Unreachable screen. `stoppedAt` is when `agent_stopping` arrived, null when the agent just vanished.
  private checkTimer: ReturnType<typeof setTimeout> | null = null;
  private stoppedAt: number | null = null;
  private checking = false;
  private episodeAfterCheck = false;

  // Heartbeat and rekey timer of the live session
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private rekeyTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly PING_INTERVAL_MS = 10_000;

  // A frozen tab hears nothing, so the silence window restarts when it becomes visible again.
  private lastVisibleAt = 0;
  private removeVisibilityListener: (() => void) | null = null;

  /**
   * Both timers hold their own session's socket and transport rather than reading `this.ws`: a
   * frame of an old transport on a newer session's socket would kill that session.
   */
  private startHeartbeat(ws: RelayWs, t: LiveTransport): void {
    this.stopHeartbeat();
    if (typeof document !== 'undefined') {
      const onVisibility = () => {
        if (document.visibilityState === 'visible') this.lastVisibleAt = Date.now();
      };
      document.addEventListener('visibilitychange', onVisibility);
      this.removeVisibilityListener = () => document.removeEventListener('visibilitychange', onVisibility);
    }
    const tick = () => {
      try {
        if (t.rekey_due()) ws.send(t.rekey_frame());
        const ping = new TextEncoder().encode(JSON.stringify({ type: 'ping', ts: Date.now() }));
        ws.send(t.encrypt_frame(ping));
      } catch {
        /* a failing send means the socket is going down; the decrypt loop will handle it */
      }
    };
    this.heartbeat = setInterval(tick, PairingDriver.PING_INTERVAL_MS);
    // The agent trusts a browser only once it decrypts the browser's first frame, so that frame must not wait a tick.
    tick();
    this.rekeyTimer = setInterval(() => {
      try {
        ws.send(t.rekey_frame());
      } catch {
        /* as for a ping */
      }
    }, this.deps.rekeyIntervalMs ?? REKEY_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
    if (this.rekeyTimer !== null) {
      clearInterval(this.rekeyTimer);
      this.rekeyTimer = null;
    }
    this.removeVisibilityListener?.();
    this.removeVisibilityListener = null;
  }

  /**
   * Next inbound message of a live session, or `silence` once the window passes with none.
   * After `silence` the caller must close `ws` and never read it again.
   */
  private async nextLive(ws: RelayWs, sawFrame: boolean): Promise<WsMessage | WsClose | { kind: 'silence' }> {
    const windowMs = sawFrame ? SILENCE_TIMEOUT_MS : PRE_HELLO_SILENCE_TIMEOUT_MS;
    let wait = windowMs;
    while (true) {
      const msg = await ws.next(wait);
      if (msg.kind !== 'timeout') return msg;
      const sinceVisible = Date.now() - this.lastVisibleAt;
      if (sinceVisible >= windowMs) return { kind: 'silence' };
      wait = windowMs - sinceVisible;
    }
  }

  constructor(private readonly deps: DriverDeps) {
    const [s, set] = createSignal<AppState>({ kind: 'idle' });
    this.state = s;
    this.setState = set;
  }

  async start(parsed: ParsedPairing, browserPriv: Uint8Array): Promise<void> {
    this.setState({ kind: 'connecting', sid: parsed.sid });

    try {
      await this.deps.ensureWasm();
    } catch (e) {
      this.setState({ kind: 'error', code: 'wasm_load_failed', detail: extractMessage(e) });
      return;
    }

    let ws: RelayWs;
    try {
      ws = await this.deps.openWs(parsed.sid);
    } catch (e) {
      this.setState({ kind: 'error', code: 'ws_open_failed', detail: extractMessage(e) });
      return;
    }
    this.ws = ws;

    let bundle: { hs: HandshakeAdapter; agentFingerprint: string };
    try {
      bundle = this.deps.makeHandshake(parsed.pin, browserPriv, parsed.agentPub);
    } catch (e) {
      this.setState({ kind: 'error', code: 'handshake_failed', detail: extractMessage(e) });
      ws.close();
      return;
    }

    this.setState({ kind: 'handshaking', sid: parsed.sid, agentFp: bundle.agentFingerprint });
    ws.send(bundle.hs.spake_msg_a());

    // First post-send message: spake_msg_b (binary) or text close-notice.
    const msgB = await ws.next(HANDSHAKE_REPLY_TIMEOUT_MS);
    if (msgB.kind === 'timeout') {
      this.failHandshakeTimeout(ws);
      return;
    }
    if (msgB.kind === 'close') {
      this.transitionFromClose(msgB.code, msgB.reason);
      return;
    }
    if (msgB.kind === 'text') {
      if (this.handleText(msgB.data)) return;
      this.setState({ kind: 'error', code: 'handshake_failed', detail: 'unexpected text frame' });
      ws.close();
      return;
    }

    let noiseMsg1: Uint8Array;
    try {
      noiseMsg1 = bundle.hs.process_spake_msg_b(msgB.data);
    } catch (e) {
      this.setState({ kind: 'error', code: 'handshake_failed', detail: extractMessage(e) });
      ws.close();
      return;
    }
    ws.send(noiseMsg1);

    // Second post-send message: noise msg2 (binary) or text/close.
    const msg2 = await ws.next(HANDSHAKE_REPLY_TIMEOUT_MS);
    if (msg2.kind === 'timeout') {
      this.failHandshakeTimeout(ws);
      return;
    }
    if (msg2.kind === 'close') {
      this.transitionFromClose(msg2.code, msg2.reason);
      return;
    }
    if (msg2.kind === 'text') {
      if (this.handleText(msg2.data)) return;
      this.setState({ kind: 'error', code: 'handshake_failed', detail: 'unexpected text frame' });
      ws.close();
      return;
    }

    try {
      bundle.hs.process_noise_msg2(msg2.data);
    } catch (e) {
      this.setState({ kind: 'error', code: 'handshake_failed', detail: extractMessage(e) });
      ws.close();
      return;
    }

    const browserFp = this.deps.browserFingerprint(browserPriv);
    // Capture reconnect material so a mid-session drop can auto-resume.
    this.agentFp = bundle.agentFingerprint;
    this.browserFp = browserFp;
    this.browserPriv = browserPriv;
    this.agentPub = parsed.agentPub;
    try {
      const root = bundle.hs.reconnect_root();
      this.reconnectRoot = root;
      await this.deps.persistReconnect(parsed.agentPub, root);
    } catch (e) {
      console.warn('failed to persist reconnect record', e);
    }
    this.setState({ kind: 'live', agentFp: bundle.agentFingerprint, browserFp });
    this.startHeartbeat(ws, bundle.hs);

    // Decrypt loop — fire-and-forget; state is updated inside.
    void this.runDecryptLoop(bundle.hs);
  }

  private failHandshakeTimeout(ws: RelayWs): void {
    ws.close();
    this.setState({ kind: 'error', code: 'handshake_timeout' });
  }

  reset(): void {
    this.removeWakeups();
    this.clearCheck();
    this.stopHeartbeat();
    this.ws?.close();
    this.ws = null;
    this.setState({ kind: 'idle' });
  }

  private async runDecryptLoop(hs: HandshakeAdapter): Promise<void> {
    if (!this.ws) return;
    const ws = this.ws;
    let sawFrame = false;
    let decrypted = false;
    while (true) {
      const msg = await this.nextLive(ws, sawFrame);
      sawFrame = true;
      if (msg.kind === 'close' || msg.kind === 'silence') {
        if (msg.kind === 'silence') ws.close();
        this.freshSessionLost(msg);
        return;
      }
      if (msg.kind === 'text') {
        if (this.handleText(msg.data)) return;
        // Unknown text post-handshake is ignored (forward compat).
        continue;
      }
      let plain: Uint8Array | null;
      try {
        plain = hs.decrypt_frame(msg.data);
      } catch (e) {
        if (e instanceof PeerClosed) {
          ws.close();
          this.freshSessionLost({ kind: 'close', code: 1000, reason: 'peer_closed' });
        } else {
          // No reconnect episode is under way after a fresh pairing: this failure opens one.
          if (!decrypted) this.episodeStart = Date.now();
          this.endOnDecryptFailure(ws, e, decrypted);
        }
        return;
      }
      decrypted = true;
      if (plain !== null && this.handlePayload(plain)) return;
    }
  }

  /** A fresh-pairing session is gone: resume it if the reconnect record was captured. */
  private freshSessionLost(msg: WsClose | { kind: 'silence' }): void {
    if (this.reconnectRoot && this.browserPriv && this.agentPub) {
      this.reconnectAfterLoss();
      return;
    }
    this.stopHeartbeat();
    if (msg.kind === 'close') {
      this.transitionFromClose(msg.code, msg.reason);
    } else {
      this.setState({ kind: 'error', code: 'disconnected', detail: 'inbound silence' });
    }
  }

  /** The live session dropped: a new reconnect episode, starting with an immediate attempt. */
  private reconnectAfterLoss(): void {
    this.stopHeartbeat();
    this.installWakeups();
    this.retry = 0;
    void this.reconnectLoop();
  }

  /**
   * A live frame failed to decrypt: the session is over (SPEC §B.12), so end it and reconnect. A
   * session that never decrypted a frame is no recovered connection but one more failed attempt
   * of the episode — same `retry`, same budget — so a failure that recurs on every session backs
   * off and ends unreachable instead of re-handshaking in a tight loop.
   */
  private endOnDecryptFailure(ws: RelayWs, e: unknown, decrypted: boolean): void {
    console.warn('live frame failed to decrypt', extractMessage(e));
    this.stopHeartbeat();
    ws.close(DECRYPT_FAILURE_CLOSE_CODE, 'decrypt_failure');
    if (!this.reconnectRoot || !this.browserPriv || !this.agentPub) {
      this.setState({ kind: 'error', code: 'disconnected', detail: 'decrypt_failure' });
      return;
    }
    if (decrypted) {
      this.reconnectAfterLoss();
      return;
    }
    this.installWakeups();
    // The loop backs off before its next attempt; the dead session must not show live meanwhile.
    this.setState({ kind: 'reconnecting', agentFp: this.agentFp, browserFp: this.browserFp });
    void this.reconnectLoop(true);
  }

  /** Parse and dispatch a decrypted JSON frame; true if it ended the session. */
  private handlePayload(plain: Uint8Array): boolean {
    try {
      const text = new TextDecoder().decode(plain);
      const obj = JSON.parse(text);
      if (obj && obj.type === 'agent_stopping') {
        this.stopHeartbeat();
        if (obj.reason === 'forgotten') {
          // The agent dropped this device's trust: no restart is coming, so no checks and no kept record.
          this.ws?.close();
          void this.terminal('forgotten', 'forgotten');
          return true;
        }
        this.enterUnreachable(Date.now());
        this.ws?.close();
        this.retry = 0;
        return true;
      }
      this.deps.dispatchFrame(obj);
    } catch (e) {
      console.warn('failed to parse decrypted frame', e);
    }
    return false;
  }

  /**
   * Resume an existing session from a persisted reconnect record (no QR/PIN).
   * Resolves once the auto-reconnect loop reaches `live` or a terminal error.
   */
  async reconnect(
    reconnectRoot: Uint8Array,
    browserPriv: Uint8Array,
    agentPub: Uint8Array,
    agentFp: string,
  ): Promise<void> {
    this.reconnectRoot = reconnectRoot;
    this.browserPriv = browserPriv;
    this.agentPub = agentPub;
    this.agentFp = agentFp;
    this.browserFp = this.deps.browserFingerprint(browserPriv);
    this.installWakeups();
    await this.reconnectLoop();
  }

  /**
   * One reconnect attempt; resolves true if it ended in `live` or terminal, false to retry. A
   * `silent` one is a check from Unreachable, which stays on screen until it succeeds.
   */
  private async attemptReconnect(silent = false): Promise<boolean> {
    if (!this.reconnectRoot || !this.browserPriv || !this.agentPub) return false;
    if (!silent) this.setState({ kind: 'reconnecting', agentFp: this.agentFp, browserFp: this.browserFp });
    try {
      await this.deps.ensureWasm();
    } catch {
      return false;
    }
    let rc: ReconnectAdapter;
    try {
      rc = this.deps.makeReconnect(this.reconnectRoot, this.browserPriv, this.agentPub);
    } catch (e) {
      await this.terminal('reconnect_denied', extractMessage(e));
      return true;
    }
    const sid = this.deps.rendezvousId(this.reconnectRoot);
    let ws: RelayWs;
    try {
      ws = await this.deps.openWs(sid);
    } catch {
      return false;
    }
    this.ws = ws;
    ws.send(rc.msg1());
    const msg2 = await ws.next(HANDSHAKE_REPLY_TIMEOUT_MS);
    if (msg2.kind === 'timeout') {
      // Never read this socket again: a late reply would sit unconsumed in its queue.
      ws.close();
      return false;
    }
    if (msg2.kind === 'close') return await this.handleReconnectClose(msg2.code, msg2.reason);
    if (msg2.kind === 'text' || msg2.kind === 'binary') {
      const data = msg2.kind === 'binary' ? msg2.data : new TextEncoder().encode(msg2.data);
      const denyReason = this.verifiedDenyReason(data);
      if (denyReason) { await this.terminal(denyReason, denyReason); return true; }
      if (msg2.kind === 'text') { ws.close(); return false; } // non-deny text → retry
    }
    try {
      rc.process_msg2((msg2 as { data: Uint8Array }).data);
    } catch {
      ws.close();
      return false;
    }
    // A decrypt failure before the first frame keeps `retry` (endOnDecryptFailure), so it is
    // not reset here but by the first decrypted frame. A close, silence or peer close before
    // that frame still resets it through reconnectAfterLoss (bug register L17).
    // A wakeup armed by unreachable/agent_stopping would otherwise survive this success and
    // re-dial the rendezvous room its own live leg still holds (4409 room_busy).
    this.removeWakeups();
    this.clearCheck();
    this.setState({ kind: 'live', agentFp: this.agentFp, browserFp: this.browserFp });
    this.startHeartbeat(ws, rc);
    void this.runReconnectDecryptLoop(rc);
    return true;
  }

  /** Try to read a binary frame as a deny notice. Returns the reason if it is a
   *  well-formed, MAC-verified deny; null otherwise (caller treats as msg2). */
  private verifiedDenyReason(frame: Uint8Array): 'forgotten' | 'expired' | null {
    let obj: any;
    try { obj = JSON.parse(new TextDecoder().decode(frame)); } catch { return null; }
    if (!obj || obj.type !== 'reconnect_denied') return null;
    const reason = obj.reason === 'expired' ? 'expired' : obj.reason === 'forgotten' ? 'forgotten' : null;
    if (!reason || typeof obj.mac !== 'string' || !this.reconnectRoot) return null;
    let mac: Uint8Array;
    try { mac = Uint8Array.from(atob(obj.mac), (c) => c.charCodeAt(0)); } catch { return null; }
    const rid = this.deps.rendezvousId(this.reconnectRoot);
    return this.deps.verifyDenyMac(this.reconnectRoot, reason, rid, mac) ? reason : null;
  }

  /** Returns true if the close is terminal (stop looping), false if retriable. */
  private async handleReconnectClose(code: number, reason: string): Promise<boolean> {
    const cls = classifyCloseCode(code, reason);
    if (cls === 'reconnect_denied') {
      await this.terminal('reconnect_denied', `${code} ${reason}`);
      return true;
    }
    // unknown_session / room_expired / room_busy / rate_limited / disconnected → retriable.
    return false;
  }

  /**
   * `afterFailedAttempt`: a session that was already set up has just failed as an attempt
   * (endOnDecryptFailure), so the episode goes on with a pause instead of an immediate attempt.
   */
  private async reconnectLoop(afterFailedAttempt = false): Promise<void> {
    if (this.reconnecting) return; // a loop or a check is already in flight — do not race it
    this.clearCheck();
    this.reconnecting = true;
    try {
      if (this.retry === 0 && !afterFailedAttempt) this.episodeStart = Date.now();
      let attempt = !afterFailedAttempt;
      while (true) {
        if (attempt) {
          const attemptStart = Date.now();
          if (await this.attemptReconnect()) return;
          if (Date.now() - attemptStart > ATTEMPT_MAX_MS + FROZEN_TAB_SLACK_MS) this.episodeStart = Date.now();
        }
        attempt = true;
        this.retry += 1;
        const pause = backoffMs(this.retry, Date.now() - this.episodeStart);
        // A pause with no attempt after it would only hold "Reconnecting…" over a lost cause.
        if (Date.now() + pause - this.episodeStart >= RECONNECT_BUDGET_MS) break;
        const pauseStart = Date.now();
        const woken = await this.sleepWithWakeup(pause);
        // A wakeup (network back, user looking) or a thaw is a new chance: on an exhausted
        // budget it would end in unreachable with nothing tried, so it opens a fresh episode,
        // fast-retry window included.
        if (woken || Date.now() - pauseStart > pause + FROZEN_TAB_SLACK_MS) this.episodeStart = Date.now();
      }
      this.enterUnreachable(null);
    } finally {
      this.reconnecting = false;
    }
  }

  /**
   * The only way into `unreachable`. The record stays, so a returning agent reconnects without a
   * new QR: a wakeup (online, the page becoming visible) resumes the loop, and checks look for it.
   */
  private enterUnreachable(stoppedAt: number | null): void {
    this.removeWakeups();
    this.setState({ kind: 'unreachable', agentFp: this.agentFp, browserFp: this.browserFp });
    this.installWakeups();
    this.wakeup = () => this.requestEpisode();
    this.stoppedAt = stoppedAt;
    this.scheduleCheck();
  }

  private scheduleCheck(): void {
    this.clearCheck();
    const sinceStop = this.stoppedAt === null ? null : Date.now() - this.stoppedAt;
    this.checkTimer = setTimeout(() => {
      this.checkTimer = null;
      void this.check();
    }, unreachableCheckDelayMs(sinceStop));
  }

  private clearCheck(): void {
    if (this.checkTimer !== null) clearTimeout(this.checkTimer);
    this.checkTimer = null;
  }

  /**
   * One silent attempt from `unreachable`. A hidden page neither checks nor re-arms: becoming
   * visible wakes a whole episode instead.
   */
  private async check(): Promise<void> {
    if (this.state().kind !== 'unreachable' || this.reconnecting) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    this.reconnecting = true;
    this.checking = true;
    this.episodeAfterCheck = false;
    let done = false;
    try {
      done = await this.attemptReconnect(true);
    } catch (e) {
      // One more failed check: letting it escape would switch the checks off for good.
      console.warn('check from unreachable failed', extractMessage(e));
    } finally {
      this.reconnecting = false;
      this.checking = false;
    }
    const episode = this.episodeAfterCheck;
    this.episodeAfterCheck = false;
    if (done) return;
    if (episode) {
      if (this.state().kind !== 'reconnecting') return; // reset() took the screen meanwhile
      this.retry = 0;
      void this.reconnectLoop(true); // the check was the episode's first attempt: a pause, then the next
    } else if (this.state().kind === 'unreachable') {
      this.scheduleCheck();
    }
  }

  /**
   * "Retry now" or a wakeup from `unreachable`. During a check it must neither dial next to it nor
   * wait for it unseen: the screen shows "Reconnecting…" at once, and the check becomes the
   * episode's first attempt.
   */
  private requestEpisode(): void {
    if (this.checking) {
      if (this.state().kind !== 'unreachable') return; // already turned into an episode, or reset
      this.episodeAfterCheck = true;
      this.episodeStart = Date.now(); // reconnectLoop(true) keeps it
      this.setState({ kind: 'reconnecting', agentFp: this.agentFp, browserFp: this.browserFp });
      return;
    }
    this.retry = 0;
    void this.reconnectLoop();
  }

  /** Manually trigger a reconnect attempt, resetting the retry counter. */
  retryReconnectNow(): void {
    this.requestEpisode();
  }

  private async terminal(code: ErrorCode, detail: string): Promise<void> {
    this.removeWakeups();
    this.clearCheck();
    if (code === 'forgotten' || code === 'expired' || code === 'reconnect_denied') {
      try {
        await this.deps.clearReconnect();
      } catch {
        /* best effort */
      }
    }
    this.setState({ kind: 'error', code, detail });
  }

  private async runReconnectDecryptLoop(rc: ReconnectAdapter): Promise<void> {
    if (!this.ws) return;
    const ws = this.ws;
    let sawFrame = false;
    let decrypted = false;
    while (true) {
      const msg = await this.nextLive(ws, sawFrame);
      sawFrame = true;
      if (msg.kind === 'close' || msg.kind === 'silence') {
        if (msg.kind === 'silence') ws.close();
        this.reconnectAfterLoss();
        return;
      }
      if (msg.kind === 'text') {
        if (this.handleText(msg.data)) { this.removeWakeups(); return; }
        continue;
      }
      let plain: Uint8Array | null;
      try {
        plain = rc.decrypt_frame(msg.data);
      } catch (e) {
        if (e instanceof PeerClosed) {
          ws.close();
          this.reconnectAfterLoss();
        } else {
          this.endOnDecryptFailure(ws, e, decrypted);
        }
        return;
      }
      if (!decrypted) {
        decrypted = true;
        this.retry = 0; // the attempt that set up this session has succeeded only now
      }
      if (plain !== null && this.handlePayload(plain)) return;
    }
  }

  private installWakeups(): void {
    if (typeof window === 'undefined' || this._removeWakeups) return;
    const fire = () => {
      this.wakeup?.();
    };
    // A hidden tab must not start an episode or cut a retry pause short: the wakeup means "the page became visible" (SPEC §H.7.2).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fire();
    };
    window.addEventListener('online', fire);
    document.addEventListener('visibilitychange', onVisibility);
    this._removeWakeups = () => {
      window.removeEventListener('online', fire);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }

  private removeWakeups(): void {
    this._removeWakeups?.();
    this._removeWakeups = null;
    this.wakeup = null;
  }

  /** Sleep ms, but wake immediately if an online/visibility event fires; resolves true if woken. */
  private sleepWithWakeup(ms: number): Promise<boolean> {
    return new Promise((resolve) => {
      const t = setTimeout(() => {
        this.wakeup = null;
        resolve(false);
      }, ms);
      this.wakeup = () => {
        clearTimeout(t);
        this.wakeup = null;
        resolve(true);
      };
    });
  }

  /** Returns true if text was a known close-notice and state was set to error. */
  private handleText(text: string): boolean {
    const parsed = parseRelayNotification(text);
    if (parsed?.type === 'room_expired') {
      this.setState({ kind: 'error', code: 'room_expired' });
      this.ws?.close();
      return true;
    }
    return false;
  }

  private transitionFromClose(code: number, reason: string): void {
    const cls = classifyCloseCode(code, reason);
    if (cls === 'disconnected') {
      this.setState({ kind: 'error', code: 'disconnected', detail: `${code} ${reason}` });
    } else if (cls === 'ws_open_failed') {
      this.setState({ kind: 'error', code: 'ws_open_failed' });
    } else {
      this.setState({ kind: 'error', code: cls });
    }
  }
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

/** Exponential backoff (capped 30s, or 5s early in an episode) with full-ish jitter. `attempt` is 1-based. */
export function backoffMs(attempt: number, elapsedMs: number): number {
  let base = Math.min(30_000, 250 * 2 ** Math.min(attempt, 7));
  if (elapsedMs < FAST_RETRY_WINDOW_MS) base = Math.min(base, FAST_RETRY_CAP_MS);
  return Math.floor(base / 2 + Math.random() * (base / 2));
}

/** Delay to the next check on the Unreachable screen; `sinceStopMs` is the time since `agent_stopping`, null without one. */
export function unreachableCheckDelayMs(sinceStopMs: number | null): number {
  const fast = sinceStopMs !== null && sinceStopMs < STOPPED_FAST_WINDOW_MS;
  const base = fast ? STOPPED_CHECK_MS : UNREACHABLE_CHECK_MS;
  const jitter = fast ? STOPPED_CHECK_JITTER_MS : UNREACHABLE_CHECK_JITTER_MS;
  // Floored before the sum: a sum near the top of the range can round up onto its open end.
  return base - jitter + Math.floor(Math.random() * 2 * jitter);
}
